<template>
  <div
    ref="videoPlayer"
    class="video-player"
  >
    <transition name="fade">
      <div class="video-player__container">
        <!-- Controls progress bar/timer -->
        <div
          v-if="videoModal"
          class="video-player__controls-progress-container"
        >
          <span
            ref="timeCurrent"
            class="video-player__time-current text-body-xs"
          />

          <div
            ref="progressBar"
            class="video-player__progress-bar"
          >
            <div
              ref="progressFill"
              class="video-player__progress-filled"
            />
          </div>

          <span
            ref="timeTotal"
            class="video-player__time-total text-body-xs"
          />
        </div>

        <!-- Controls -->
        <div
          class="video-player__controls"
          :class="`video-player__controls-position--${videoControls.iconsPosition}`"
        >
          <button
            v-show="videoControls.showSound"
            class="video-player__button"
            type="button"
            @click="handleToggleAudio"
          >
            <component :is="audioIcon" />

            <span
              class="visually-hidden"
              v-text="audioText"
            />
          </button>

          <button
            v-show="videoControls.showPlayPause"
            class="video-player__button"
            type="button"
            @click="handleControlClick"
          >
            <component :is="controlIcon" />

            <span
              class="visually-hidden"
              v-text="controlText"
            />
          </button>

          <button
            class="video-player__button video-player__button--fullscreen"
            type="button"
            aria-label="string('accessibility.video.fullscreen')"
            @click="handleFullscreenClick"
          >
            <icon-fullscreen />
          </button>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="subtitles"
        class="video-player__subtitle"
        :class="{ [$class('active')]: displaySubtitles }"
      >
        <button
          type="button"
          :aria-label="$string('accessibility.video.subtitles')"
          @click="displaySubtitles = !displaySubtitles"
          v-text="$string('accessibility.video.cc')"
        />
      </div>
    </transition>

    <video
      ref="video"
      class="video-player__video"
      controlslist="false"
      crossorigin="anonymous"
      muted
      :loop="!videoModal"
      disablepictureinpicture
      playsinline
      :aria-label="controlText"
      :autoplay="videoControls.autoplay"
      @keyup.enter="handleControlClick"
      @pause="state = 'paused'"
      @play="state = 'playing'"
      @muted="audio = false"
      @load="setVideoLoaded"
    >
      <source
        :src="video"
        type="video/mp4"
      >

      <track
        v-if="subtitles"
        kind="subtitles"
        label="English"
        :src="subtitles"
        srclang="en"
      >
    </video>
  </div>
</template>

<script>

/**
 * Vue: Video Player (video-player)
 * -----------------------------------------------------------------------------
 * Video player functionality.
 *
 * @param {Object} [image] - Shopify image object.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {String} video - Video URL.
 * @param {Object} videoControls - Video controls.
 *
 */
import { nextTick } from 'vue'
import { mapState } from 'vuex'

import IconAudio from '~icons/video/audio.svg'
import IconMute from '~icons/video/mute.svg'
import IconPause from '~icons/video/pause.svg'
import IconPlay from '~icons/video/play.svg'
import IconFullscreen from '~icons/video/fullscreen.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconAudio,
    IconMute,
    IconPause,
    IconPlay,
    IconFullscreen,
  },

  props: {
    // image: {
    //   type: Object,
    //   default: () => ({
    //     alt: '',
    //     height: 0,
    //     src: '',
    //     width: 0,
    //   }),
    // },
    subtitles: {
      type: String,
      default: '',
    },
    video: {
      type: String,
      default: '',
    },
    videoControls: {
      type: Object,
      default: () => ({
        videoEnable: true,
        videoModalEnable: false,
        autoplay: '',
        showSound: '',
        iconsPosition: '',
      }),
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    videoModal: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'video-loaded': (payload) => payload,
  },

  data() {
    return {
      displaySubtitles: false,
      showOverlay: true,
      state: 'loading',
      audio: false,
      fullscreen: false,
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.state) {
        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    controlIcon() {
      switch (this.state) {
        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    audioText() {
      switch (this.audio) {
        case true:
          return this.$string('accessibility.video.mute')

        case false:
        default:
          return this.$string('accessibility.video.unmute')
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    audioIcon() {
      switch (this.audio) {
        case true:
          return 'icon-audio'

        case false:
        default:
          return 'icon-mute'
      }
    },
  },

  watch: {

    /**
     * Watch overlay is active.
     */
    isActive() {
      if (this.isActive) {
        nextTick().then(() => {
          this.startVideoWithAudio()

          if (this.isMobile) {
            // When user rotate device to landscape enter fullscreen
            window.matchMedia('(orientation: portrait)').addEventListener('change', (event) => {
              const portrait = event.matches

              if (!portrait && this.isActive) {
                this.enterFullscreen()
              }
            })
          }
        })
      } else {
        nextTick().then(() => {
          this.resetVideoToStart()
          this.pauseVideoAndMuteAudio()
          this.exitFullscreen()
        })
      }
    },

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },
  },

  mounted() {
    this.handleVideoLoad()
    this.setEventListeners()

    /**
     * Reset any subtitles that browser has enabled by default.
     */
    if (!this.$refs.video.textTracks.length) {
      return
    }

    Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
      this.$refs.video.textTracks[index].mode = 'hidden'
    })

    if (this.videoControls.autoplay) {
      this.playVideo()
    }
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {
      if (this.videoModal) {
        this.$refs.video.addEventListener('timeupdate', this.updateProgressBar)
        this.$refs.video.addEventListener('canplay', this.updateProgressBar)
        this.$refs.progressBar.addEventListener('click', this.setProgressBar)
        this.$refs.videoPlayer.addEventListener('mousemove', this.setClass)
        this.$refs.video.addEventListener('ended', this.resetVideoToStart)

        /**
         * Handles video play/pause when clicking on video element.
         */
        this.$refs.videoPlayer.addEventListener('click', (event) => {
          const target = event.target
          const videoElement = target.querySelector('.video-player__video')

          if (videoElement) {
            this.handleControlClick()
          }
        })

        /**
         * Detect when user on fullscreen.
         */
        const prefixes = [
          '',
          'moz',
          'webkit',
          'ms',
          'onwebkit',
          'webkitIs',
        ]

        prefixes.forEach((prefix) => {
          document.addEventListener(`${prefix}fullscreenchange`, () => {
            if (
              document.fullscreenElement ||
              document.mozFullScreenElement ||
              document.webkitFullscreenElement ||
              document.msFullscreenElement
            ) {
              this.fullscreen = true
              this.$refs.videoPlayer.classList.add('is-fullscreen')
              this.$refs.video.setAttribute('controls', true)

            } else {
              this.fullscreen = false
              this.$refs.videoPlayer.classList.remove('is-fullscreen')
              this.$refs.video.removeAttribute('controls')
              this.exitFullscreen()
            }
          })
        })
      }
    },

    /**
     * Set class on mouse hover.
     */
    setClass() {
      this.$refs.videoPlayer.classList.add('active')

      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        this.$refs.videoPlayer.classList.remove('active')
      }, 1500)
    },

    /**
     * Handle video load.
     * - Fixes Safari (15.6+) jump issue on initial load.
     */
    handleVideoLoad() {
      if (this.$refs?.video) {
        this.$refs.video.addEventListener('canplay', () => {
          this.$refs.video.style.opacity = '1'
        })
      }
    },

    /**
     * Handle control and video click.
     */
    handleControlClick() {
      if (this.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Handle toggle audio.
     */
    handleToggleAudio() {
      if (this.audio) {
        this.$refs.video.muted = 'muted'
        this.audio = false
      } else {
        this.$refs.video.muted = false
        this.audio = true
      }
    },

    /**
     * Start video with audio.
     */
    startVideoWithAudio() {
      this.$refs.video.muted = false
      this.audio = true
      this.playVideo()
    },

    /**
     * Pause video and mute audio.
     */
    pauseVideoAndMuteAudio() {
      this.$refs.video.muted = 'muted'
      this.audio = false
      this.pauseVideo()
    },

    /**
     * Reset video to start.
     */
    resetVideoToStart() {
      this.$refs.video.currentTime = 0
    },

    /**
     * Play video.
     */
    async playVideo() {
      try {
        await this.$refs.video.play()
        this.showOverlay = false
        this.state = 'playing'
      } catch (error) {
        reportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      try {
        await this.$refs.video.pause()
        this.state = 'paused'
      } catch (error) {
        reportError('Failed to pause video', error)
      }
    },

    /**
     * Set image as loaded.
     */
    setVideoLoaded() {
      setTimeout(() => {
        this.$emit('video-loaded', true)
      }, this.$timing('quick'))
    },

    /**
     * Handle fullscreen click.
     */
    handleFullscreenClick() {
      this.enterFullscreen()
    },

    /**
     * Enter fullscreen.
     */
    enterFullscreen() {
      const video = this.$refs.video

      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen()
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen()
      } else if (video.webkitRequestFullScreen) {
        video.webkitRequestFullScreen()
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen()
      } else if (video.webkitSupportsFullscreen) {
        video.webkitEnterFullscreen()
      }
    },

    /**
     * Exit fullscreen.
     */
    exitFullscreen() {
      const video = this.$refs.video

      if (video.exitFullscreen) {
        video.exitFullscreen()
      } else if (video.mozCancelFullScreen) {
        video.mozCancelFullScreen()
      } else if (video.webkitExitFullscreen) {
        video.webkitExitFullscreen()
      }
    },

    /**
     * Neat Time.
     */
    neatTime(time) {
      const minutes = Math.floor((time % 3600) / 60)
      let seconds = Math.floor(time % 60)
      seconds = seconds > 9 ? seconds : `0${seconds}`
      return `${minutes}:${seconds}`
    },

    /**
     * Update progress.
     */
    updateProgressBar() {
      this.$refs.progressFill.style.width = `${this.$refs.video.currentTime / this.$refs.video.duration * 100}%`
      this.$refs.timeCurrent.innerHTML = `${this.neatTime(this.$refs.video.currentTime)}`
      this.$refs.timeTotal.innerHTML = this.neatTime(this.$refs.video.duration)
    },

    /**
     * Set progress.
     */
    setProgressBar(event) {
      const newTime = event.offsetX / this.$refs.progressBar.offsetWidth
      this.$refs.progressFill.style.width = `${newTime * 100}%`
      this.$refs.video.currentTime = newTime * this.$refs.video.duration
    },
  },
}
</script>

<style lang="scss">
@import "./video-player";
</style>
